import { useEffect, useRef } from 'react'
import { isAxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type {
  AnswerInput, AuditorDto, CollaborationDto, CompanyDto, ExportFormat, ExportKind, FileDownloadUrl, FindingDto, FindingInput, FormInstanceDto, FormInstanceListItem, FormInstanceStatus, FormKind,
  JoinableReportDto, Paged, PhotoDto, ReportPulseDto, TemplateSummary, TemplateVersion,
} from './types'

export interface ExportedFileDto {
  id: string; kind: ExportKind; status: 'Queued' | 'Processing' | 'Ready' | 'Failed'; version: number; fileId?: string; fileName?: string; sizeBytes?: number
  isCurrent: boolean; isStale: boolean; generatedAt?: string; error?: string
}
export interface AttachmentDto { id: string; formInstanceId: string; kind: string; title?: string; signerName?: string; signedAt?: string; fileId: string; fileName: string; contentType: string; sizeBytes: number; createdAt: string }

export interface DashboardSummary {
  openActions: number; overdueActions: number; dueSoonActions: number; draftReports: number; completedThisMonth: number; totalReports: number
  recentAudits: { id: string; code?: string; title?: string; templateCode: string; companyName: string; status: FormInstanceStatus; auditDate?: string; percent?: number; grade?: string; findingCount: number }[]
  gradeDistribution: { grade: string; count: number }[]
  scoreTrend: { companyId: string; companyName: string; date: string; percent: number; grade: string; code?: string }[]
  weakGroups: { number: number; name: string; averagePercent: number; sampleCount: number }[]
  companyScores: { companyId: string; companyName: string; latestPercent?: number; latestGrade?: string; openActions: number; auditCount: number }[]
}

export interface ReportFilters {
  page?: number; pageSize?: number; sort?: string; search?: string; companyId?: string; templateId?: string; status?: FormInstanceStatus | ''; kind?: FormKind | ''
  from?: string; to?: string; grade?: string; mineOnly?: boolean
}

const qs = (params: Record<string, unknown>) =>
  Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null && v !== false).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')

export const keys = {
  dashboard: (companyId?: string) => ['dashboard', companyId ?? 'all'] as const,
  reports: (f: ReportFilters) => ['reports', f] as const,
  report: (id: string) => ['report', id] as const,
  exports: (id: string) => ['exports', id] as const,
  attachments: (id: string) => ['attachments', id] as const,
  collaboration: (id: string) => ['collaboration', id] as const,
  pulse: (id: string) => ['pulse', id] as const,
  joinable: ['joinable-reports'] as const,
  templates: ['templates'] as const,
  template: (id: string) => ['template', id] as const,
  companies: ['companies'] as const,
}

export const useDashboard = (companyId?: string) =>
  useQuery({ queryKey: keys.dashboard(companyId), queryFn: async () => (await api.get<DashboardSummary>('/v1/dashboard/summary', { params: { companyId } })).data })

export const useReports = (filters: ReportFilters) =>
  useQuery({ queryKey: keys.reports(filters), queryFn: async () => (await api.get<Paged<FormInstanceListItem>>(`/v1/form-instances?${qs({ ...filters })}`)).data, placeholderData: (prev) => prev })

export const useReport = (id: string | undefined) =>
  useQuery({ queryKey: keys.report(id ?? ''), queryFn: async () => (await api.get<FormInstanceDto>(`/v1/form-instances/${id}`)).data, enabled: !!id })

export const useExports = (id: string | undefined) =>
  useQuery({ queryKey: keys.exports(id ?? ''), queryFn: async () => (await api.get<ExportedFileDto[]>(`/v1/form-instances/${id}/exports`)).data, enabled: !!id, refetchInterval: (q) => (q.state.data?.some((e) => e.status === 'Queued' || e.status === 'Processing') ? 3000 : false) })

export const useAttachments = (id: string | undefined) =>
  useQuery({ queryKey: keys.attachments(id ?? ''), queryFn: async () => (await api.get<AttachmentDto[]>(`/v1/form-instances/${id}/attachments`)).data, enabled: !!id })

export const useTemplates = () => useQuery({ queryKey: keys.templates, queryFn: async () => (await api.get<TemplateSummary[]>('/v1/templates')).data })
export const useTemplateVersion = (versionId: string | undefined) =>
  useQuery({ queryKey: keys.template(versionId ?? ''), queryFn: async () => (await api.get<TemplateVersion>(`/v1/templates/versions/${versionId}`)).data, enabled: !!versionId, staleTime: 5 * 60_000 })
export const useCompanies = () => useQuery({ queryKey: keys.companies, queryFn: async () => (await api.get<Paged<CompanyDto>>('/v1/companies?pageSize=200')).data.items })

export async function fileUrl(fileId: string): Promise<string> {
  const { data } = await api.get<FileDownloadUrl>(`/v1/files/${fileId}/download-url`)
  return data.url
}

export function useReportMutations(id: string) {
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: keys.report(id) })
    void qc.invalidateQueries({ queryKey: ['reports'] })
    void qc.invalidateQueries({ queryKey: keys.exports(id) })
  }
  const saveAnswers = useMutation({
    mutationFn: async (answers: AnswerInput[]) => (await api.put<FormInstanceDto>(`/v1/form-instances/${id}/answers`, answers)).data,
    onSuccess: (d) => {
      void qc.cancelQueries({ queryKey: keys.report(id) }) // a live-refresh load that started before this save must not put older answers back
      qc.setQueryData(keys.report(id), d)
    },
  })
  const upsertFinding = useMutation({ mutationFn: async (input: FindingInput) => (await api.put<FindingDto>(`/v1/form-instances/${id}/findings`, input)).data, onSuccess: invalidate })
  const deleteFinding = useMutation({ mutationFn: async (findingId: string) => api.delete(`/v1/form-instances/${id}/findings/${findingId}`), onSuccess: invalidate })
  const updateHeader = useMutation({
    mutationFn: async (body: { title?: string; siteId?: string; headerValues?: Record<string, unknown>; auditors?: Omit<AuditorDto, 'sortOrder'>[]; notes?: string }) =>
      (await api.put<FormInstanceDto>(`/v1/form-instances/${id}/header`, body)).data,
    onSuccess: (d) => qc.setQueryData(keys.report(id), d),
  })
  const complete = useMutation({ mutationFn: async (allowUnanswered: boolean) => (await api.post<FormInstanceDto>(`/v1/form-instances/${id}/complete?allowUnanswered=${allowUnanswered}`)).data, onSuccess: invalidate })
  // "Revize et": back to Draft with revisionNo + 1. The returned report goes straight into the cache so the editing page opens as a draft.
  const reopen = useMutation({
    mutationFn: async () => (await api.post<FormInstanceDto>(`/v1/form-instances/${id}/reopen`)).data,
    onSuccess: (d) => { qc.setQueryData(keys.report(id), d); invalidate() },
  })
  const approve = useMutation({ mutationFn: async () => (await api.post<FormInstanceDto>(`/v1/form-instances/${id}/approve`)).data, onSuccess: invalidate })
  // Free-form report items: the note autosaves while typing, so the cached report is patched in place instead of refetched.
  const saveItem = useMutation({
    mutationFn: async (input: FindingInput) => (await api.put<FindingDto>(`/v1/form-instances/${id}/findings`, input)).data,
    onSuccess: (saved) => {
      // A report load that started before this save would put the older list back (live refresh re-loads often): drop it, the next pulse re-loads.
      void qc.cancelQueries({ queryKey: keys.report(id) })
      qc.setQueryData<FormInstanceDto>(keys.report(id), (prev) => {
        if (!prev) return prev
        const exists = prev.findings.some((f) => f.id === saved.id)
        return { ...prev, findings: exists ? prev.findings.map((f) => (f.id === saved.id ? { ...f, ...saved } : f)) : [...prev.findings, saved] }
      })
      void qc.invalidateQueries({ queryKey: ['reports'] })
    },
  })
  const reorderFindings = useMutation({
    mutationFn: async (orderedIds: string[]) => api.put(`/v1/form-instances/${id}/findings/order`, orderedIds),
    onMutate: (orderedIds) => {
      qc.setQueryData<FormInstanceDto>(keys.report(id), (prev) => {
        if (!prev) return prev
        const position = new Map(orderedIds.map((x, i) => [x, i + 1]))
        return { ...prev, findings: prev.findings.map((f) => (position.has(f.id) ? { ...f, sortOrder: position.get(f.id)! } : f)) }
      })
    },
    onSettled: invalidate,
  })
  const uploadPhoto = useMutation({
    mutationFn: async (p: { file: File; questionId?: string; findingId?: string; clientId?: string; sortOrder?: number; caption?: string }) => {
      const form = new FormData()
      form.append('file', p.file)
      form.append('clientId', p.clientId ?? crypto.randomUUID())
      if (p.questionId) form.append('questionId', p.questionId)
      if (p.findingId) form.append('findingId', p.findingId)
      if (p.caption) form.append('caption', p.caption)
      if (p.sortOrder) form.append('sortOrder', String(p.sortOrder))
      return (await api.post<PhotoDto>(`/v1/form-instances/${id}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 })).data
    },
    onSuccess: invalidate,
  })
  const reorderPhotos = useMutation({ mutationFn: async (items: { photoId: string; sortOrder: number }[]) => (await api.put<PhotoDto[]>(`/v1/form-instances/${id}/photos/order`, items)).data, onSuccess: invalidate })
  const deletePhoto = useMutation({ mutationFn: async (photoId: string) => api.delete(`/v1/form-instances/${id}/photos/${photoId}`), onSuccess: invalidate })
  const requestExport = useMutation({ mutationFn: async (kind: 'excel' | 'pdf') => (await api.post(`/v1/form-instances/${id}/exports/${kind}`)).data, onSuccess: invalidate })
  const attachmentsChanged = () => {
    void qc.invalidateQueries({ queryKey: keys.attachments(id) })
    void qc.invalidateQueries({ queryKey: keys.exports(id) })
  }
  // Optional company logo (PowerPoint cover + slides). The server replaces the previous logo and drops the cached export files.
  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      form.append('kind', LOGO_KIND)
      return (await api.post<AttachmentDto>(`/v1/form-instances/${id}/attachments`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 })).data
    },
    onSuccess: attachmentsChanged,
  })
  const deleteAttachment = useMutation({ mutationFn: async (attachmentId: string) => api.delete(`/v1/form-instances/${id}/attachments/${attachmentId}`), onSuccess: attachmentsChanged })
  // Soft delete: the report disappears for the whole tenant (only the platform admin's "Silinmiş Raporlar" page keeps it).
  const remove = useMutation({
    mutationFn: async () => { await api.delete(`/v1/form-instances/${id}`) },
    onSuccess: () => { qc.removeQueries({ queryKey: keys.report(id) }); void qc.invalidateQueries({ queryKey: ['reports'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }) },
  })
  // Platform admin: closes a report stuck in progress, skipping the completion checks.
  const forceClose = useMutation({ mutationFn: async () => (await api.post<FormInstanceDto>(`/v1/form-instances/${id}/complete?force=true`)).data, onSuccess: invalidate })
  return {
    saveAnswers, upsertFinding, deleteFinding, saveItem, reorderFindings, updateHeader, complete, reopen, approve, uploadPhoto, reorderPhotos, deletePhoto, requestExport,
    uploadLogo, deleteAttachment, invalidate, remove, forceClose,
  }
}

// ---------------------------------------------------------------- shared reports: participants, join requests, live refresh

export const LOGO_KIND = 'CompanyLogo'
export const LOGO_ACCEPT = 'image/png,image/jpeg'
export const isLogoFile = (f: File) => f.type === 'image/png' || f.type === 'image/jpeg' || (f.type === '' && /\.(png|jpe?g)$/i.test(f.name))
/** The report's current logo: the newest CompanyLogo attachment. */
export const logoOf = (attachments: AttachmentDto[] | undefined) =>
  [...(attachments ?? [])].filter((a) => a.kind === LOGO_KIND).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]

/** Mirrors FormInstanceScope.IsOwner: the creator (or anybody when the creator is unknown) and tenant/super admins run the report. */
export const isReportManager = (user: { id: string; roles: { role: string }[] } | null | undefined, r: { ownerUserId?: string | null } | undefined) =>
  !!user && !!r && (user.roles.some((x) => x.role === 'SuperAdmin' || x.role === 'TenantAdmin') || !r.ownerUserId || r.ownerUserId === user.id)

/** Owner, participants and — for the owner — join requests. Refreshed by the pulse; `refetchInterval` is for screens without one. */
export const useCollaboration = (id: string | undefined, enabled = true, refetchInterval: number | false = false) =>
  useQuery({
    queryKey: keys.collaboration(id ?? ''),
    queryFn: async () => (await api.get<CollaborationDto>(`/v1/form-instances/${id}/collaboration`)).data,
    enabled: !!id && enabled,
    refetchInterval,
    retry: false,
  })

/** Draft reports of my tenant I could ask to join (empty for viewers / suppliers). */
export const useJoinableReports = (enabled = true) =>
  useQuery({ queryKey: keys.joinable, queryFn: async () => (await api.get<JoinableReportDto[]>('/v1/form-instances/joinable')).data, enabled, refetchInterval: 20_000, staleTime: 0 })

/** "Katılma isteği gönder" (also re-requests after a rejection). */
export function useRequestCollaboration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.post<CollaborationDto>(`/v1/form-instances/${id}/collaboration/request`)).data,
    onSuccess: (d) => {
      qc.setQueryData(keys.collaboration(d.instanceId), d)
      void qc.invalidateQueries({ queryKey: keys.joinable })
      void qc.invalidateQueries({ queryKey: ['reports'] })
    },
  })
}

/** Owner: approve / reject / invite / remove. Participants: remove(own id) = leave the report. */
export function useCollaborationMutations(id: string) {
  const qc = useQueryClient()
  const done = (d?: CollaborationDto) => {
    if (d) qc.setQueryData(keys.collaboration(id), d)
    else void qc.invalidateQueries({ queryKey: keys.collaboration(id) })
    void qc.invalidateQueries({ queryKey: keys.report(id) })
    void qc.invalidateQueries({ queryKey: ['reports'] })
    void qc.invalidateQueries({ queryKey: keys.joinable })
  }
  const approve = useMutation({ mutationFn: async (userId: string) => (await api.post<CollaborationDto>(`/v1/form-instances/${id}/collaboration/${userId}/approve`)).data, onSuccess: done })
  const reject = useMutation({ mutationFn: async (userId: string) => (await api.post<CollaborationDto>(`/v1/form-instances/${id}/collaboration/${userId}/reject`)).data, onSuccess: done })
  const invite = useMutation({ mutationFn: async (userId: string) => (await api.post<CollaborationDto>(`/v1/form-instances/${id}/collaboration/invite`, { userId })).data, onSuccess: done })
  const remove = useMutation({ mutationFn: async (userId: string) => { await api.delete(`/v1/form-instances/${id}/collaboration/${userId}`) }, onSuccess: () => done() })
  return { approve, reject, invite, remove }
}

export const PULSE_INTERVAL_MS = 4000

/**
 * Live refresh of a report screen: polls the cheap pulse endpoint (paused while the tab is hidden, caught up when it is shown again)
 * and re-loads the report, its participants and attachments whenever the stamp changes — other people's additions show up within seconds.
 * `live` drives the green "canlı" dot; `lost` means the report is no longer reachable (removed from it, or it was deleted).
 */
export function useReportPulse(id: string | undefined, enabled = true, intervalMs = PULSE_INTERVAL_MS) {
  const qc = useQueryClient()
  const pulse = useQuery({
    queryKey: keys.pulse(id ?? ''),
    queryFn: async () => (await api.get<ReportPulseDto>(`/v1/form-instances/${id}/pulse`)).data,
    enabled: !!id && enabled,
    refetchInterval: intervalMs,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
  const stamp = pulse.data?.stamp
  const seen = useRef<{ id: string; stamp: string } | null>(null)

  useEffect(() => {
    if (!id || !stamp) return
    const previous = seen.current?.id === id ? seen.current.stamp : undefined
    seen.current = { id, stamp }
    if (previous === stamp) return
    if (previous === undefined) {
      // First beat: a report that came from the cache may be older than this stamp — re-load it unless it was just fetched.
      const loadedAt = qc.getQueryState(keys.report(id))?.dataUpdatedAt ?? 0
      if (Date.now() - loadedAt < 5000) return
    }
    void qc.invalidateQueries({ queryKey: keys.report(id) })
    void qc.invalidateQueries({ queryKey: keys.collaboration(id) })
    void qc.invalidateQueries({ queryKey: keys.attachments(id) })
    void qc.invalidateQueries({ queryKey: keys.exports(id) })
  }, [id, stamp, qc])

  const status = pulse.isError && isAxiosError(pulse.error) ? pulse.error.response?.status : undefined
  return { data: pulse.data, live: !!id && enabled && pulse.isSuccess, lost: status === 404 || status === 403 }
}

// ---------------------------------------------------------------- downloads (Word / Excel / PDF / PowerPoint)

export const EXPORT_FORMATS: ExportFormat[] = ['word', 'excel', 'pdf', 'powerpoint']
export const exportKindOf: Record<ExportFormat, ExportKind> = { word: 'Word', excel: 'Excel', pdf: 'Pdf', powerpoint: 'PowerPoint' }

/** Starts a browser download of a signed URL (same-origin in production, proxied in dev). */
export function triggerDownload(url: string, fileName?: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = fileName ?? ''
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/** POST /form-instances/{id}/exports/{kind}/download — the file is generated on the spot when missing or stale, hence the long timeout. */
export async function downloadReportExport(id: string, format: ExportFormat): Promise<FileDownloadUrl> {
  const { data } = await api.post<FileDownloadUrl>(`/v1/form-instances/${id}/exports/${format}/download`, null, { timeout: 180_000 })
  triggerDownload(data.url, data.fileName)
  return data
}

// ---------------------------------------------------------------- free-form report ("Boş Rapor": photos + notes only)

export const FREE_TEMPLATE_CODE = 'SERBEST'
export const isFreeTemplateCode = (code?: string | null) => (code ?? '').toUpperCase() === FREE_TEMPLATE_CODE
export const isFreeVersion = (v?: Pick<TemplateVersion, 'settings'> | null) => v?.settings?.freeForm === true
/** Where a draft is filled in: the item editor for free-form reports, the question form for everything else. */
export const fillPath = (r: { id: string; templateCode?: string | null }) => `/reports/${r.id}/${isFreeTemplateCode(r.templateCode) ? 'free' : 'fill'}`

export class FreeReportSetupError extends Error {
  code: 'template_missing' | 'company_missing'
  constructor(code: 'template_missing' | 'company_missing') {
    super(code)
    this.code = code
  }
}

let startingFreeReport: Promise<FormInstanceDto> | null = null

/** Creates a SERBEST draft for the tenant's own company (first company when there is none). Single-flight, so a double click or a re-run effect never creates two drafts. */
export function useStartFreeReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => {
      startingFreeReport ??= (async () => {
        const templates = await qc.fetchQuery({ queryKey: keys.templates, queryFn: async () => (await api.get<TemplateSummary[]>('/v1/templates')).data })
        const template = templates.find((x) => isFreeTemplateCode(x.code) && x.currentVersionId)
        if (!template) throw new FreeReportSetupError('template_missing')
        const companies = await qc.fetchQuery({ queryKey: keys.companies, queryFn: async () => (await api.get<Paged<CompanyDto>>('/v1/companies?pageSize=200')).data.items })
        const company = companies.find((c) => c.type === 'Self' && c.isActive) ?? companies.find((c) => c.type === 'Self') ?? companies.find((c) => c.isActive) ?? companies[0]
        if (!company) throw new FreeReportSetupError('company_missing')
        const created = (await api.post<FormInstanceDto>('/v1/form-instances', { templateId: template.id, companyId: company.id, source: 'Web' })).data
        qc.setQueryData(keys.report(created.id), created)
        void qc.invalidateQueries({ queryKey: ['reports'] })
        return created
      })().finally(() => { startingFreeReport = null })
      return startingFreeReport
    },
  })
}

// POST /form-instances/{id}/exports/email — sends the report's Word/Excel/PDF/PowerPoint exports by e-mail.
export interface EmailReportInput { kinds: ExportKind[]; to?: string[]; fileName?: string | null }
export interface EmailReportResult { sentTo: string[]; attachments: { fileName: string; sizeBytes: number }[] }
export const useEmailReport = (id: string) =>
  useMutation({ mutationFn: async (body: EmailReportInput) => (await api.post<EmailReportResult>(`/v1/form-instances/${id}/exports/email`, body)).data })

export function useCreateReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { templateId: string; companyId: string; siteId?: string; title?: string; headerValues?: Record<string, unknown>; auditors?: { userId?: string; displayName: string; isLead: boolean }[]; auditStartTime?: string }) =>
      (await api.post<FormInstanceDto>('/v1/form-instances', { ...body, source: 'Web' })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reports'] }),
  })
}

// ---------------------------------------------------------------- actions & notifications (Phase 5)

export type ActionStatus = 'Open' | 'InProgress' | 'ClosureRequested' | 'Closed' | 'Overdue' | 'Rejected'
export interface ActionListItem {
  id: string; actionNo: string; status: ActionStatus; actionText: string; responsibleName: string; responsibleUserId?: string; responsibleCompanyId?: string; dueDate?: string; isOverdue: boolean; daysLeft?: number
  formInstanceId: string; reportCode?: string; templateCode: string; companyId: string; companyName: string; findingId: string; findingNumber?: number; severity: 'Minor' | 'Major' | 'ZeroTolerance'; findingDescription: string
  questionNo?: number; closureRequestedAt?: string; closedAt?: string; createdAt: string; syncVersion: number
}
export interface ActionDetail {
  summary: ActionListItem; questionText?: string; closureNote?: string; closureRequestedByName?: string; verifiedByName?: string; verificationNote?: string
  findingPhotos: PhotoDto[]; evidencePhotos: PhotoDto[]; history: { at: string; userEmail?: string; change: string }[]
}
export interface ActionFilters { page?: number; pageSize?: number; sort?: string; search?: string; status?: ActionStatus | ''; companyId?: string; mineOnly?: boolean; overdueOnly?: boolean; openOnly?: boolean }
export interface NotificationDto { id: string; kind: string; title: string; body: string; link?: string; actionId?: string; formInstanceId?: string; createdAt: string; readAt?: string }

export const useActions = (f: ActionFilters) =>
  useQuery({ queryKey: ['actions', f], queryFn: async () => (await api.get<Paged<ActionListItem>>(`/v1/actions?${qs({ ...f })}`)).data, placeholderData: (prev) => prev })
export const useAction = (id: string | undefined) =>
  useQuery({ queryKey: ['action', id], queryFn: async () => (await api.get<ActionDetail>(`/v1/actions/${id}`)).data, enabled: !!id })

export function useActionMutations(id: string) {
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['action', id] })
    void qc.invalidateQueries({ queryKey: ['actions'] })
    void qc.invalidateQueries({ queryKey: ['dashboard'] })
  }
  const start = useMutation({ mutationFn: async () => (await api.post<ActionListItem>(`/v1/actions/${id}/start`)).data, onSuccess: invalidate })
  const requestClosure = useMutation({ mutationFn: async (note: string) => (await api.post<ActionListItem>(`/v1/actions/${id}/request-closure`, { note })).data, onSuccess: invalidate })
  const verify = useMutation({ mutationFn: async (p: { approve: boolean; note?: string }) => (await api.post<ActionListItem>(`/v1/actions/${id}/verify`, p)).data, onSuccess: invalidate })
  const reassign = useMutation({
    mutationFn: async (p: { responsibleUserId?: string; responsibleCompanyId?: string; responsibleName?: string; dueDate?: string; actionText?: string }) => (await api.patch<ActionListItem>(`/v1/actions/${id}`, p)).data,
    onSuccess: invalidate,
  })
  const uploadEvidence = useMutation({
    mutationFn: async (p: { formInstanceId: string; file: File }) => {
      const form = new FormData()
      form.append('file', p.file)
      form.append('clientId', crypto.randomUUID())
      form.append('actionId', id)
      return (await api.post<PhotoDto>(`/v1/form-instances/${p.formInstanceId}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data
    },
    onSuccess: invalidate,
  })
  return { start, requestClosure, verify, reassign, uploadEvidence }
}

export const useNotifications = (unreadOnly = false) =>
  useQuery({ queryKey: ['notifications', unreadOnly], queryFn: async () => (await api.get<Paged<NotificationDto>>(`/v1/notifications?pageSize=30&unreadOnly=${unreadOnly}`)).data, refetchInterval: 60_000 })
export const useUnreadCount = () =>
  useQuery({ queryKey: ['notifications', 'unread-count'], queryFn: async () => (await api.get<number>('/v1/notifications/unread-count')).data, refetchInterval: 60_000 })
export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: async (p: { ids?: string[]; all?: boolean }) => (await api.post<number>('/v1/notifications/read', p)).data, onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }) })
}
